import { ContentStatus, STATUS_ANZEIGE, UserRole } from '../types';
import type { RückfrageWunsch } from '../components/ui/Rueckfrage';

/**
 * Was der Moderation gehört — und was das CMS der Redaktion deshalb nicht anbietet.
 *
 * ⚠️ Das ist KEIN Schutz — der Server ist die Wache und bleibt es: `wendeModerationsregelnAn`
 * (`energetic-eternity-backend/src/services/contentMapping.ts`) prüft an jedem Schreibweg, die
 * Prüf-Routen hängen hinter `requireModeratorOrAdmin` bzw. `authorize(['ADMIN', 'MODERATOR'])`.
 * Diese Weiche ist EHRLICHKEIT: ohne sie sähe eine CONTENT_CREATORin Bedienungen, die ihr der Server
 * jedes Mal abweist — ein Angebot, das die Anwendung nicht einhalten kann.
 *
 * Drei Regeln des Servers spiegelt das CMS hier (Tom 2026-10-02):
 *   - Veröffentlichen ist Moderation (#1361) — der Übergang nach PUBLISHED ist für die Redaktion 403.
 *   - Die Bezahlschranke `isPremium` ändert nur die Moderation (10:11:54Z, #1367) — ein unveränderter
 *     Wert geht durch, eine Änderung ist 403.
 *   - Speichert die Redaktion ein veröffentlichtes Werk, fällt es auf den Entwurf, bis die Moderation es
 *     wieder freigibt (09:44Z, F3, #1368). Ein ausdrücklich gewähltes „Entwurf"/„Archiviert" gilt.
 * „Abgelehnt" bietet das Formular niemandem an (`STATUS_WAEHLBAR`, Tom 12:22:32Z: nur die Moderation).
 *
 * Ein `Set` aus den Enum-Werten statt eines Vergleichs auf Zeichenketten: die Werte sind der Vertrag zum
 * Prisma-Enum `UserRole`, und ein Tippfehler in einem Literal fiele nirgends auf. Verglichen wird über
 * `String(...).toUpperCase()`, weil die Rolle aus `localStorage` zurückkommen kann (`AuthContext` liest
 * den gespeicherten Benutzer beim Start) — dieselbe Normalisierung fährt `Sidebar.tsx`.
 */
const MODERATIONS_ROLLEN: ReadonlySet<string> = new Set([UserRole.ADMIN, UserRole.MODERATOR]);

export const istModeration = (rolle: unknown): boolean =>
  MODERATIONS_ROLLEN.has(String(rolle ?? '').toUpperCase());

/** Ob eine Stufe im Status-Auswahlfeld für diese Rolle gesperrt steht. */
export const stufeGesperrt = (stufe: ContentStatus, darfModerieren: boolean): boolean =>
  stufe === ContentStatus.PUBLISHED && !darfModerieren;

/**
 * Beschriftung einer Stufe im Status-Auswahlfeld.
 *
 * Die gesperrte Stufe sagt, wer sie vergibt — außer sie ist der aktuelle Stand: ein veröffentlichtes
 * Werk zeigt schlicht „Veröffentlicht", was es mit dem Speichern wird, sagt der Hinweis darunter.
 */
export const stufenText = (stufe: ContentStatus, darfModerieren: boolean, bestandStatus: string | undefined): string =>
  stufeGesperrt(stufe, darfModerieren) && bestandStatus !== stufe
    ? MODERATIONS_TEXTE.stufeGesperrt
    : STATUS_ANZEIGE[stufe]?.text ?? stufe;

/**
 * Ob das Speichern ein veröffentlichtes Werk auf den Entwurf zurückwirft (F3).
 *
 * Maßgeblich ist der BESTAND (der Status, mit dem das Formular geöffnet wurde), nicht die Auswahl:
 * die Regel des Servers fragt, ob das Werk vor dem Speichern öffentlich war. Wer selbst „Entwurf" oder
 * „Archiviert" wählt, nimmt das Werk bewusst heraus — das gilt beim Server und braucht keine Warnung.
 */
export const fälltAufEntwurf = (
  darfModerieren: boolean,
  bestandStatus: string | undefined,
  gewählterStatus: string | undefined,
): boolean =>
  !darfModerieren
  && bestandStatus === ContentStatus.PUBLISHED
  && gewählterStatus !== ContentStatus.DRAFT
  && gewählterStatus !== ContentStatus.ARCHIVED;

/**
 * Wortlaut: einheitlich „Sie" — das CMS siezt (Tom 2026-10-02 14:05:09Z über ⭐️, „Okay ich folge beiden
 * Empfehlungen"; Formulierungen von 💎, BACKLOG `cms-moderationstexte-wortlaut`).
 *
 * Der Blog nennt seine Einträge überall „Beitrag", nicht „Werk" — Hinweis und Rückfrage dort brauchen das
 * Wort der Fläche (`…Beitrag`). Beitrag ist männlich: er/ihn statt es.
 */
export const MODERATIONS_TEXTE = {
  stufeGesperrt: 'Veröffentlicht – gibt die Moderation frei',
  statusHinweis: 'Veröffentlichen übernimmt die Moderation. Speichern Sie Ihr Werk als Entwurf.',
  rückfallHinweis:
    'Dieses Werk ist veröffentlicht. Wenn Sie Änderungen speichern, wird es wieder zum Entwurf und ist '
    + 'nicht mehr öffentlich, bis die Moderation es erneut freigibt.',
  rückfallHinweisBeitrag:
    'Dieser Beitrag ist veröffentlicht. Wenn Sie Änderungen speichern, wird er wieder zum Entwurf und ist '
    + 'nicht mehr öffentlich, bis die Moderation ihn erneut freigibt.',
  premiumHinweis: 'Ob ein Inhalt Premium ist, legt die Moderation fest.',
} as const;

/**
 * Die Rückfrage beim Speichern, wenn `fälltAufEntwurf` gilt.
 *
 * Bewusst OHNE `gefahr`: nichts wird gelöscht, die Änderung ist gespeichert und die Moderation gibt das
 * Werk wieder frei. Aber das Werk verschwindet aus der App, bis das geschieht — das soll niemand erst
 * nach dem Speichern erfahren. Der Hinweis am Statusfeld allein reicht nicht: das Feld steht am Ende
 * des Formulars, gespeichert wird aus der festen Leiste, auch nach einer Änderung ganz oben.
 */
export const RÜCKFALL_RÜCKFRAGE: RückfrageWunsch = {
  titel: 'Werk wird wieder zum Entwurf',
  text:
    MODERATIONS_TEXTE.rückfallHinweis
    + '\n\nSoll es öffentlich bleiben, lassen Sie die Moderation die Änderung vornehmen.',
  bestätigenText: 'Als Entwurf speichern',
};

/** Dieselbe Rückfrage am Blog — mit dem Wort der Fläche. */
export const RÜCKFALL_RÜCKFRAGE_BEITRAG: RückfrageWunsch = {
  titel: 'Beitrag wird wieder zum Entwurf',
  text:
    MODERATIONS_TEXTE.rückfallHinweisBeitrag
    + '\n\nSoll er öffentlich bleiben, lassen Sie die Moderation die Änderung vornehmen.',
  bestätigenText: 'Als Entwurf speichern',
};
